import { __rest, __assign } from 'tslib';
import React, { forwardRef } from 'react';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import '@popperjs/core';

var sizeVariants = {
    xxs: "tw-h-2 tw-w-2",
    xs: "tw-h-3 tw-w-3",
    sm: "tw-h-3.5 tw-w-3.5",
    md: "tw-h-4 tw-w-4",
    lg: "tw-h-5 tw-w-5",
    xl: "tw-h-6 tw-w-6",
    xxl: "tw-h-7 tw-w-7",
    "3xl": "tw-h-8 tw-w-8",
    "4xl": "tw-h-10 tw-w-10",
    "5xl": "tw-h-12 tw-w-12"
};
var defaultProps = {
    addClassName: "",
    color: "currentColor",
    secondaryColor: "currentColor",
    iconProps: {}
};
function getIconPathData(iconDef) {
    var path = iconDef.icon[4];
    if (Array.isArray(path)) {
        return path;
    }
    return [path];
}
function getViewBox(iconDef) {
    var width = iconDef.icon[0];
    var height = iconDef.icon[1];
    return "0 0 ".concat(width, " ").concat(height);
}
function setIconElement(IconElement, iconProps) {
    var style = __assign(__assign({}, IconElement.props.style), iconProps);
    var className = IconElement.props.className;
    var Element = React.createElement(IconElement.type, { style: style, className: className });
    return Element;
}
/**
 * Renders Font Awesome icons.
 *
 * [Check out the Font Awesome icon gallery.](https://fontawesome.com/icons?d=gallery)
 *
 * To use, import the icon definition from the appropriate font awesome library:
 *
 * ```jsx
 * import { faAbacus, faFire } from "@fortawesome/pro-solid-svg-icons";
 * ```
 *
 * And pass the definition to the `FaIcon` component:
 *
 * ```jsx
 * <FaIcon faIconDef={faFire} />
 * ```
 *
 * If the icon has semantic meaning, use the `aria-label` attribute to make it accessible to all users. If no
 * `aria-label` attribute is provided, it will be assumed that the icon is purely for decoration and it will
 * be `aria-hidden`.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { FaIcon } from "@panwds/react-ui";
 * ```
 */
var FaIcon = forwardRef(function (props, ref) {
    var 
    // eslint-disable-next-line react/prop-types
    addClassName = props.addClassName, 
    // eslint-disable-next-line react/prop-types
    color = props.color, 
    // eslint-disable-next-line react/prop-types
    faIconDef = props.faIconDef, 
    // eslint-disable-next-line react/prop-types
    IconElement = props.IconElement, 
    // eslint-disable-next-line react/prop-types
    iconProps = props.iconProps, 
    // eslint-disable-next-line react/prop-types
    text = props.text, 
    // eslint-disable-next-line react/prop-types
    fontIconClass = props.fontIconClass, 
    // eslint-disable-next-line react/prop-types
    secondaryColor = props.secondaryColor, 
    // eslint-disable-next-line react/prop-types
    _a = props.size, 
    // eslint-disable-next-line react/prop-types
    size = _a === void 0 ? "md" : _a, 
    // eslint-disable-next-line react/prop-types
    imgSrc = props.imgSrc, svgProps = __rest(props, ["addClassName", "color", "faIconDef", "IconElement", "iconProps", "text", "fontIconClass", "secondaryColor", "size", "imgSrc"]);
    var a11yProps = svgProps["aria-label"]
        ? {}
        : { "aria-hidden": true, focusable: false };
    var classes = classNames("tw-inline-block tw-overflow-visible tw-align-middle", sizeVariants[size], addClassName);
    return faIconDef ? (React.createElement("svg", __assign({}, a11yProps, { role: "img", viewBox: getViewBox(faIconDef) }, svgProps, { className: classes, ref: ref }), getIconPathData(faIconDef).map(function (path, index) { return (React.createElement("path", { fill: index === 0 ? color : secondaryColor, d: path, key: index })); }))) : fontIconClass ? (React.createElement("i", { className: "tw-inline-block tw-overflow-visible ".concat(fontIconClass) })) : IconElement ? (React.createElement(React.Fragment, null, setIconElement(IconElement, iconProps))) : text ? (React.createElement("span", { className: addClassName }, text)) : (React.createElement("img", { className: classes, src: imgSrc }));
});
FaIcon.displayName = "FaIcon";
FaIcon.defaultProps = defaultProps;

export { FaIcon };
//# sourceMappingURL=FaIcon.esm.js.map
